import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, FolderKanban, FileText, MessageSquare } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useI18n } from "../../context/I18nContext";

export const Overview = ({ onSwitch }) => {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [projects, setProjects] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}),
      api.get("/quotes").then((r) => setQuotes(r.data)).catch(() => {}),
      api.get("/messages").then((r) => setMessages(r.data)).catch(() => {}),
    ]);
  }, []);

  const fmt = (n) =>
    new Intl.NumberFormat(lang === "fr" ? "fr-FR" : "en-GB", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(n);

  const cards = [
    { id: "projects", label: t("dash.card_projects"), value: projects.length, icon: FolderKanban },
    { id: "quotes", label: t("dash.card_quotes"), value: quotes.length, icon: FileText },
    { id: "chat", label: t("dash.card_messages"), value: messages.length, icon: MessageSquare },
  ];

  const labelType = (id) =>
    id === "vitrine" ? t("sim.type_vitrine") : id === "ecommerce" ? t("sim.type_ecom") : t("sim.type_app");

  return (
    <div data-testid="dashboard-overview" className="space-y-10">
      <div className="border border-white/10 p-8 sm:p-10 bg-zinc-900/30">
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">{t("dash.greeting")}</p>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tighter">{user?.name}.</h2>
        <p className="text-zinc-400 max-w-xl mt-4 text-sm">{t("dash.greeting_intro")}</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/simulateur"
            data-testid="overview-cta-simulateur"
            className="inline-flex items-center gap-2 bg-white text-zinc-950 hover:bg-zinc-200 px-5 py-3 text-sm"
          >
            {t("dash.cta_quote")} <ArrowUpRight size={14} />
          </Link>
          <button
            type="button"
            onClick={() => onSwitch("chat")}
            data-testid="overview-cta-chat"
            className="inline-flex items-center gap-2 border border-white/20 hover:border-white/60 px-5 py-3 text-sm"
          >
            {t("dash.cta_chat")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 border border-white/10">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <button
              key={c.id}
              data-testid={`overview-card-${c.id}`}
              onClick={() => onSwitch(c.id)}
              className="bg-zinc-950 hover:bg-zinc-900 transition-colors p-8 text-left"
            >
              <div className="flex items-center justify-between mb-8">
                <Icon size={20} strokeWidth={1.4} className="text-zinc-300" />
                <ArrowUpRight size={14} className="text-zinc-500" />
              </div>
              <p className="font-display text-5xl tracking-tighter">{c.value}</p>
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-3">{c.label}</p>
            </button>
          );
        })}
      </div>

      {quotes.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">{t("dash.recent_quotes")}</p>
          <div className="border border-white/10 divide-y divide-white/10">
            {quotes.slice(0, 3).map((q) => (
              <div key={q.quote_id} className="flex items-center justify-between p-5 bg-zinc-950">
                <div>
                  <p className="font-display text-lg">{labelType(q.params?.site_type)}</p>
                  <p className="text-xs text-zinc-500 mt-1">
                    {q.params?.pages} {t("sim.pages_unit")} · {q.params?.features?.length || 0} options
                  </p>
                </div>
                <p className="font-mono text-sm">{fmt(q.total_price)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
